import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
} from "@mui/material";
import { Form, Formik } from "formik";
import React from "react";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import * as Yup from "yup";

import {
  modifyWorkerSkill,
} from "../../../../../../../../export/workers";
import useEditSkillDialogController from "./controllers/use-edit-skill-dialog-controller";
import EditSkillDialogContent from "./edit-skill-dialog-content";

interface EditWorkerSkillDialogProps {
  open: boolean;
  closeEditWorkerSkillDialog: () => void;
  onCombinedResult?: (result: {
    message: React.ReactNode;
    severity: "success" | "error" | "info";
  }) => void;
}

const EditWorkerSkillDialog: React.FC<EditWorkerSkillDialogProps> = ({
  open,
  closeEditWorkerSkillDialog,
  onCombinedResult,
}) => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const {
    formikInitialValues,
    requiredCertifications,
  } = useEditSkillDialogController();

  const schema = Yup.object().shape({
    validFrom: Yup.string().nullable(),
    validTo: Yup.string()
      .nullable()
      .test(
        "is-after-valid-from",
        t("workers_valid-until-after-valid-from"),
        function (validTo) {
          const { validFrom } = this.parent;
          if (validFrom && validTo) {
            return new Date(validTo) > new Date(validFrom);
          }
          return true;
        }
      ),
  });

  // Additional validation for validFrom to ensure it is required if validTo is provided
  schema.fields.validFrom = schema.fields.validFrom.test(
    "is-required-if-valid-to",
    t("workers_select-valid-from-date"),
    function (validFrom) {
      const { validTo } = this.parent;
      if (validTo && !validFrom) {
        return false;
      }
      return true;
    }
  );

  return (
    <Dialog
      open={open}
      onClose={() => dispatch(closeEditWorkerSkillDialog())}
      maxWidth={false}
    >
      <DialogTitle
        sx={{
          padding: "45px 45px 24px 45px",
        }}
      >
        {t("workers_edit-skill-for-worker")}
      </DialogTitle>
      <Formik
        initialValues={formikInitialValues}
        validationSchema={schema}
        enableReinitialize
        onSubmit={async (values) => {
          const resultAction = await dispatch(modifyWorkerSkill(values) as any);
          if (modifyWorkerSkill.rejected.match(resultAction)) {
            if (onCombinedResult) {
              onCombinedResult({
                message:
                  resultAction.payload ?? t("workers_modify-skill-failed"),
                severity: "error",
              });
            }
          }
        }}
      >
        {({ values, setFieldValue, errors, isSubmitting, isValid, dirty }) => {
          return (
            <Form>
              <DialogContent
                sx={{
                  padding: "0",
                }}
              >
                <EditSkillDialogContent
                  values={values}
                  setFieldValue={setFieldValue}
                  certifications={requiredCertifications}
                  errors={errors}
                />
              </DialogContent>
              <DialogActions sx={{ p: 0 }}>
                <Box
                  sx={{
                    padding: "45px",
                  }}
                >
                  <Button
                    variant={"text"}
                    sx={{ px: 2, mr: 1 }}
                    onClick={() => dispatch(closeEditWorkerSkillDialog())}
                  >
                    {t("common_cancel")}
                  </Button>
                  <Button
                    variant="contained"
                    color="primary"
                    type="submit"
                    disabled={
                      isSubmitting ||
                      !isValid ||
                      (!dirty) ||
                      !values.levelId ||
                      !!errors.validFrom ||
                      !!errors.validTo
                    }
                    startIcon={isSubmitting && <CircularProgress size={20} />}
                  >
                    {isSubmitting ? t("workers_saving") : t("common_save")}
                  </Button>
                </Box>
              </DialogActions>
            </Form>
          );
        }}
      </Formik>
    </Dialog>
  );
};

export default EditWorkerSkillDialog;
